import styles from './checkout-footer.scss';

type FooterLink = {
  ignoreQuery?: boolean;
  label: string;
  openNewTab?: 'tab' | 'modal';
  url: string;
};

type CheckoutFooterProps = {
  app?: boolean;
};

const financialLinks = [
  {
    alt: 'Digital Secured',
    src: 'https://static.johnlewiscontent.com/v1/static/digicert-secured-bw',
  },
  {
    alt: 'Amex',
    src: 'https://static.johnlewiscontent.com/v1/static/amex-bw',
  },
  {
    alt: 'Mastercard',
    src: 'https://static.johnlewiscontent.com/v1/static/mastercard-id-check-bw',
  },
  {
    alt: 'Visa',
    src: 'https://static.johnlewiscontent.com/v1/static/Visa_bw_new',
  },
];

const footerLinks: FooterLink[] = [
  {
    label: 'Contact us',
    url: 'https://www.johnlewis.com/customer-services/contact-us',
    openNewTab: 'tab',
  },
  {
    label: 'Send feedback',
    url: 'https://www.johnlewis.com/feedback',
    openNewTab: 'modal',
    ignoreQuery: true,
  },
  {
    label: 'Cookies',
    url: 'https://www.johnlewis.com/customer-services/shopping-with-us/cookies',
    openNewTab: 'tab',
  },
  {
    label: 'Refund policy',
    url: 'https://www.johnlewis.com/customer-services/returns/our-refund-policy',
    openNewTab: 'tab',
  },
  {
    label: 'Secure shopping',
    url: 'https://www.johnlewis.com/customer-services/shopping-with-us/security-and-privacy',
    openNewTab: 'tab',
  },
];

const addAppQueryParam = (url: string) => {
  const urlWithAppParam = new URL(url);
  urlWithAppParam.searchParams.set('app', 'true');
  return urlWithAppParam.toString();
};

const popupWindow = (url: string, width: number, height: number) => {
  const topWindow = window.top || window;
  const top = topWindow.screenY;
  const left = topWindow.outerWidth / 2 + topWindow.screenX - width / 2;
  const newWindow = window.open(url, '_blank', `width=${width}, height=${height}, top=${top}, left=${left}`);

  if (!newWindow) return false;

  newWindow.opener = null;
  newWindow.focus();
  return true;
};

const trackNavigation = (navigation: string) => {
  document.body.dispatchEvent(
    new CustomEvent('dataStateChange', {
      detail: {
        action: 'jl.navTracking',
        type: 'ft',
        navigation,
      },
    }),
  );
};

const CheckoutFooter = ({ app = false }: CheckoutFooterProps) => (
  <footer data-testid="checkout-footer" id="checkout-footer">
    <div className={styles.footerContainer}>
      <h2 className={styles.visuallyHidden}>Footer</h2>

      <div className={styles.financialLinksWrapper}>
        <ul className={styles.financialLinks}>
          {financialLinks.map(({ alt, src }) => (
            <li key={src}>
              <img alt={alt} aria-hidden="true" src={src} />
            </li>
          ))}
        </ul>
      </div>

      <div className={styles.footerLinksWrapper} data-testid="footer-links-wrapper">
        <div className={styles.footerLinksContainer} data-testid="footer-links-container">
          <div className={styles.footerLinks} data-testid="footer-links">
            <div className={styles.footerLinksContainer}>
              <ul>
                {footerLinks.map((footerLink) => {
                  const href = app && !footerLink.ignoreQuery ? addAppQueryParam(footerLink.url) : footerLink.url;
                  const opensNewTab = footerLink.openNewTab === 'tab' || footerLink.openNewTab === 'modal';

                  return (
                    <li key={footerLink.label}>
                      <a
                        className={styles.footerLink}
                        href={href}
                        onClick={(event) => {
                          if (footerLink.openNewTab === 'modal' && popupWindow(href, 500, 700)) {
                            event.preventDefault();
                          }
                          trackNavigation(footerLink.label);
                        }}
                        rel="noopener noreferrer"
                        target={opensNewTab ? '_blank' : undefined}
                      >
                        {footerLink.label}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.copyrightWrapper}>
        <div className={styles.termsSectionWrapper}>
          <p className={`${styles.copyright} ${styles.copyrightMiddle}`}>
            &copy; John Lewis plc 2001 - {new Date().getFullYear()}
          </p>
        </div>
      </div>
    </div>
  </footer>
);

export default CheckoutFooter;
